import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Upload } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { SimpleSelect, Field, useClauses } from "@/components/FormField";

export const EV_CATS = ["Record", "Certificate", "Report", "Training record", "Calibration certificate", "EQA report", "IQC data", "Minutes", "Photograph", "Agreement", "Other"];

export default function EvidenceUpload({ open, onClose, clauseIds = [], onDone }) {
  const clauses = useClauses();
  const ref = useRef();
  const [file, setFile] = useState(null);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setFile(null); setF({ category: "Record", clause_ids: clauseIds }); } /* eslint-disable-next-line */ }, [open]);
  const submit = async () => {
    if (!file) return toast.error("Choose a file");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", f.title || file.name);
    fd.append("category", f.category || "Record");
    fd.append("clause_ids", (f.clause_ids || []).join(","));
    fd.append("tags", f.tags || "");
    fd.append("valid_until", f.valid_until || "");
    try { await api.post("/evidence", fd); toast.success("Evidence uploaded"); onDone?.(); onClose(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="evidence-upload-dialog">
        <DialogHeader><DialogTitle className="font-display">Upload evidence</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <button type="button" onClick={() => ref.current.click()} className="w-full border-2 border-dashed rounded-lg p-5 text-sm text-[#5A6B87] hover:border-[#48217A]/50" data-testid="evidence-file-picker">
            <Upload className="h-5 w-5 mx-auto mb-1 text-[#48217A]" />{file ? file.name : "Choose file (PDF, DOCX, XLSX, CSV, JPG, PNG, TXT · max 20 MB)"}
          </button>
          <input ref={ref} type="file" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="evidence-file-input" />
          <div className="space-y-1.5"><Label>Title</Label><Input value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Defaults to file name" data-testid="evidence-title-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Category</Label><SimpleSelect value={f.category} onChange={(v) => setF({ ...f, category: v })} options={EV_CATS} testid="evidence-category-select" /></div>
            <div className="space-y-1.5"><Label>Valid until</Label><Input type="date" value={f.valid_until || ""} onChange={(e) => setF({ ...f, valid_until: e.target.value })} className="h-9" /></div>
          </div>
          <Field f={{ key: "clause_ids", label: "Linked clauses", type: "clauses" }} value={f.clause_ids} onChange={(v) => setF({ ...f, clause_ids: v })} clauses={clauses} />
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-[#48217A] hover:bg-[#3B1A66]" data-testid="evidence-upload-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Upload</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
